import { Cancel, Check, Edit, Info } from "@mui/icons-material";
import { Checkbox, Typography } from "@mui/material";
import { format, isValid, parse } from "date-fns";

import FormatISODateTime from "../../../global/FormatISODateTime";
import PermissionWrapper from "../../../global/PermissionWrapper";
import CustomIconButton from "../../../ui library/components/buttons/CustomIconButton";
import CustomHtmlTooltip from "../../../ui library/components/tooltips/custom-html-tooltip";
import ProviderTypes from "../components/ProviderType";
import ReferralFrom from "../components/ReferralFrom";
import ReferralTo from "../components/ReferralTo";
import ReferredDate from "../components/ReferredDate";
import Source from "../components/Source";
import Status from "../components/Status";
import { STRINGS } from "../utils/Strings";

export default function getReferralColumns(
  caseId,
  {
    permissions,
    selectedReferrals,
    handleCheckbox,
    editingRow,
    selectedReferralFrom,
    providerRepository,
    handleOnEditRow,
    selectedReferralTo,
    setReferralTo,
    setSelectedProviderType,
    setMode,
    referralTo,
    selectedProviderType,
    providerTypesRepository,
    mode,
    selectedReferralDate,
    selectedStatus,
    fieldOptionRepository,
    statuses,
    selectedSource,
    sources,
    handleSaveEditing,
    handleCancelEditing,
    handleStartEditing,
  }
) {
  return [
    {
      field: "id",
      headerName: "",
      width: 30,
      align: "center",
      headerAlign: "center",
      editable: false,
      renderCell: ({ row }) => (
        <>
          <PermissionWrapper permission={permissions.DELETE}>
            <div className="flex h-full items-center justify-center">
              <Checkbox
                checked={selectedReferrals.includes(row?.id)}
                onChange={(e) => handleCheckbox(e, row?.id)}
                disabled={statuses.find((s) => s.key === row?.status)?.label !== "Pending"}
              />
            </div>
          </PermissionWrapper>
        </>
      ),
    },
    {
      field: "referredFrom",
      headerName: "Referred From Provider",
      flex: 1,
      minWidth: 200,
      align: "center",
      headerAlign: "center",
      editable: false,
      renderCell: ({ row }) => (
        <>
          {editingRow && editingRow[row?.id] ? (
            <div className="flex flex-col items-center justify-center p-2">
              <ReferralFrom
                isTableView
                caseId={caseId}
                initValue={selectedReferralFrom}
                repository={providerRepository}
                onChange={(param, value) => handleOnEditRow(row?.id, param, value)}
              />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">{row?.referralFrom?.name}</div>
          )}
        </>
      ),
    },
    {
      field: "referredTo",
      headerName: "Referred To Provider",
      flex: 1,
      minWidth: 200,
      align: "center",
      headerAlign: "center",
      editable: false,
      renderCell: ({ row }) => (
        <>
          {editingRow && editingRow[row?.id] ? (
            <div className="flex flex-col items-center justify-center p-2">
              <ReferralTo
                isTableView
                initValue={selectedReferralTo}
                repository={providerRepository}
                onChange={(field, value) => {
                  handleOnEditRow(row?.id, field, value);
                  if (field === "referralTo") {
                    setReferralTo(value);
                    if (!value) {
                      handleOnEditRow(row?.id, "providerType", null);
                    }
                    setSelectedProviderType(null);
                    setMode(STRINGS.MODE.CREATE);
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center">
              <Typography className="font-inherit text-sm text-black">
                {row?.referralTo?.name}
              </Typography>
              {row?.providerType?.id ? (
                <Typography className="w-full text-center text-xs italic text-slate-500">
                  {"( " + row?.providerType.name + " )"}
                </Typography>
              ) : (
                ""
              )}
            </div>
          )}
        </>
      ),
    },
    {
      field: "providerTypes",
      headerName: "Provider Type",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      editable: false,
      renderCell: ({ row }) => {
        if (editingRow && editingRow[row?.id]) {
          return (
            <div className="flex flex-col items-start justify-center p-2">
              <ProviderTypes
                isTableView
                key={referralTo?.id || row?.id}
                mode={mode}
                initValue={selectedProviderType}
                repository={providerTypesRepository}
                referralTo={referralTo}
                onChange={(param, value) => {
                  return handleOnEditRow(row?.id, param, value);
                }}
                providerTypes={referralTo === null ? [] : referralTo?.providerTypes || []}
              />
            </div>
          );
        }
      },
    },
    {
      field: "referredDate",
      headerName: "Referred Date",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      editable: false,
      renderCell: ({ row }) => (
        <>
          {editingRow && editingRow[row?.id] ? (
            <div className="flex flex-col items-start justify-center p-2">
              <ReferredDate
                isTableView
                initValue={selectedReferralDate}
                onChange={(param, value) => {
                  handleOnEditRow(row?.id, param, value);
                }}
              />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              {row?.referredAt && isValid(parse(row.referredAt, "yyyy-MM-dd", new Date()))
                ? format(parse(row.referredAt, "yyyy-MM-dd", new Date()), "MM/dd/yyyy")
                : row?.referredAt || ""}
            </div>
          )}
        </>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      align: "center",
      headerAlign: "center",
      editable: false,
      renderCell: ({ row }) => {
        if (editingRow && editingRow[row?.id]) {
          return (
            <div className="flex flex-col items-start justify-center p-2">
              <Status
                isTableView
                initValue={selectedStatus}
                repository={fieldOptionRepository}
                onChange={(param, value) => handleOnEditRow(row?.id, param, value)}
              />
            </div>
          );
        } else {
          const statusObj = statuses.find((s) => s.key === row?.status);
          return (
            <div className="flex h-full items-center justify-center">
              {statusObj ? statusObj.label : row?.status}
            </div>
          );
        }
      },
    },
    {
      field: "source",
      headerName: "Source",
      align: "center",
      headerAlign: "center",
      editable: false,
      renderCell: ({ row }) => {
        if (editingRow && editingRow[row?.id]) {
          return (
            <div className="flex flex-col items-start justify-center p-2">
              <Source
                isTableView
                initValue={selectedSource}
                repository={fieldOptionRepository}
                onChange={(param, value) => handleOnEditRow(row?.id, param, value)}
              />
            </div>
          );
        } else {
          const sourceObj = sources.find((s) => s.key === row?.source);
          return (
            <div className="flex h-full items-center justify-center">
              {sourceObj ? sourceObj.label : row?.source}
            </div>
          );
        }
      },
    },
    {
      field: "actions",
      width: 70,
      headerName: "",
      align: "right",
      headerAlign: "center",
      editable: false,
      renderCell: ({ row }) => (
        <>
          {editingRow && editingRow[row?.id] ? (
            <div className="flex h-full items-center justify-center">
              <CustomIconButton
                iconVisible
                tooltipText="Submit"
                CustomIcon={<Check className="text-green-500" />}
                handleOnClick={() => handleSaveEditing(row?.id)}
              />
              <CustomIconButton
                iconVisible
                tooltipText="Cancel"
                CustomIcon={<Cancel className="text-red-500" />}
                handleOnClick={() => handleCancelEditing(row?.id)}
              />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <CustomIconButton
                iconVisible
                disabled={!permissions.UPDATE}
                tooltipText={`${
                  permissions.UPDATE ? "Edit Referral" : "No permission to edit the referral/s"
                }`}
                CustomIcon={
                  <Edit className={`${permissions.UPDATE ? "text-blue-700" : "text-gray-500"}`} />
                }
                handleOnClick={() => handleStartEditing(row?.id)}
              />
              <CustomHtmlTooltip
                ToolTipContent={
                  <div className="flex flex-col gap-1 p-2">
                    <Typography className="text-xs text-white">
                      {row?.createdAt && (
                        <div className="font-bold">
                          {`Created At: ${FormatISODateTime(row?.createdAt)}`}
                        </div>
                      )}
                      {row?.createdBy && row?.createdBy?.name && (
                        <div className="font-bold">{`Created By:  ${row?.createdBy?.name}`}</div>
                      )}
                    </Typography>
                    <Typography className="text-xs text-white">
                      {row?.updatedAt && (
                        <div className="font-bold">{`Updated At: ${FormatISODateTime(
                          row?.updatedAt
                        )}`}</div>
                      )}
                      {row?.updatedBy && row?.updatedBy?.name && (
                        <div className="font-bold">{`Updated By: ${row?.updatedBy?.name}`}</div>
                      )}
                    </Typography>
                  </div>
                }
              >
                <Info className="hover: cursor-pointer text-black" />
              </CustomHtmlTooltip>
            </div>
          )}
        </>
      ),
    },
  ];
}
